import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { drawingService } from '../../../services/drawingService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const UploadRevision = ({ drawing, onRevisionUploaded }) => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowed?.includes(file?.type)) {
      setError('Only PDF and image files are supported.');
      return;
    }
    setSelectedFile(file);
    setError('');
    setSuccess('');
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !drawing) return;
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Get all versions to determine next version number
      const { data: versions } = await drawingService?.getDrawingVersions(
        drawing?.site_id,
        drawing?.drawing_number
      );

      const maxVersion = versions?.reduce((max, v) => Math.max(max, v?.version_number || 0), 0) || 0;
      const nextVersion = maxVersion + 1;
      const nextRevision = `R${nextVersion}`;

      const { data, error: uploadError } = await drawingService?.uploadDrawing(selectedFile, {
        siteId: drawing?.site_id,
        companyId: drawing?.company_id,
        drawingName: drawing?.drawing_name,
        drawingNumber: drawing?.drawing_number,
        revision: nextRevision,
        discipline: drawing?.discipline,
        description: drawing?.description,
        versionNumber: nextVersion,
      });

      if (uploadError) throw uploadError;

      setSuccess(`Revision ${nextRevision} uploaded successfully.`);
      setSelectedFile(null);
      if (fileInputRef?.current) fileInputRef.current.value = '';
      onRevisionUploaded?.(data);
    } catch (err) {
      console.error('[UploadRevision] upload revision failed:', err);
      setError(err?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const canUpload = ['admin', 'site_manager']?.includes(userProfile?.role);
  if (!canUpload) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Upload" size={16} className="text-accent" />
        <h3 className="text-sm font-semibold text-text-primary">{t('uploadNewRevision')}</h3>
      </div>
      <div
        onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-background'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.svg"
          className="hidden"
          onChange={(e) => handleFileSelect(e?.target?.files?.[0])}
        />
        {selectedFile ? (
          <div className="flex items-center justify-center gap-2">
            <Icon name="FileCheck" size={20} className="text-accent" />
            <span className="text-sm font-medium text-text-primary">{selectedFile?.name}</span>
            <button
              onClick={(e) => { e?.stopPropagation(); setSelectedFile(null); }}
              className="text-text-secondary hover:text-red-500 ml-1"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        ) : (
          <>
            <Icon name="UploadCloud" size={28} className="text-text-secondary mx-auto mb-2" />
            <p className="text-sm text-text-secondary">
              {t('dragDropOr')} <span className="text-accent font-medium">{t('browse')}</span>
            </p>
            <p className="text-xs text-text-secondary mt-1">{t('pdfPngJpgWebpSupported')}</p>
          </>
        )}
      </div>
      {error && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <Icon name="AlertCircle" size={12} /> {error}
        </p>
      )}
      {success && (
        <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
          <Icon name="CheckCircle" size={12} /> {success}
        </p>
      )}
      <Button
        onClick={handleUpload}
        disabled={!selectedFile || uploading}
        className="w-full mt-3"
        size="sm"
      >
        {uploading ? (
          <span className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
            {t('uploading')}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Icon name="Upload" size={14} />
            {t('uploadRevision')}
          </span>
        )}
      </Button>
    </div>
  );
};

export default UploadRevision;
